import { groupBy, sumBy } from 'lodash';
import {
    JourneyExtra,
    JourneyExtraType,
    JourneyExtraVehicle,
} from '@ui/components/Journey/Journey.interfaces';
import { Type as IconType } from '@ui/components/Icon/Icon.interfaces';
import { FerryVehicleType } from '@shared/interfaces/ferry';
import { t } from '@shared/helpers/translation';

interface AddonItem {
    icon: IconType | undefined;
    description: string;
    type: JourneyExtraType;
}

export function getAddons(extras: JourneyExtra[], detailed: boolean): AddonItem[] {
    const items: AddonItem[] = [];

    const addons = extras.filter((extra) => extra.type !== JourneyExtraType.Vehicle);
    const vehicles = extras.filter(
        (extra) => extra.type === JourneyExtraType.Vehicle
    ) as JourneyExtraVehicle[];

    if (vehicles.length > 0) {
        Object.entries(groupBy(vehicles, (v) => `${v.vehicleType}|${v.title}`)).forEach(
            ([, values]) =>
                items.push({
                    icon: getIcon(values[0]),
                    type: values[0].type,
                    description: detailed
                        ? getDetailedDescription(values)
                        : getShortDescription(values),
                })
        );
    }

    if (addons.length > 0) {
        const group = (e: JourneyExtra) => (detailed ? `${e.type}|${e.title}` : e.type);
        Object.entries(groupBy(addons, group)).forEach(([, values]) =>
            items.push({
                icon: getIcon(values[0]),
                type: values[0].type,
                description: detailed
                    ? getDetailedDescription(values)
                    : getShortDescription(values),
            })
        );
    }

    return items;
}

function getDetailedDescription(items: (JourneyExtra | JourneyExtraVehicle)[]) {
    const [item] = items;
    const quantity = sumBy(items, 'quantity');
    return `${quantity}x ${item.title}`;
}

function getShortDescription(items: (JourneyExtra | JourneyExtraVehicle)[]) {
    const [item] = items;
    const count = sumBy(items, 'quantity');

    switch (item.type) {
        case JourneyExtraType.Accommodation:
        case JourneyExtraType.Cabin:
            return `${count}x ${t({
                key: 'shared.cabin',
                keyPlural: 'shared.cabins',
                count,
            })}`;
        case JourneyExtraType.Seat:
            return `${count}x ${t({
                key: 'shared.seat',
                keyPlural: 'shared.seats',
                count,
            })}`;
        case JourneyExtraType.Pet:
            return `${count}x ${t({
                key: 'shared.ferry_pet',
                keyPlural: 'shared.ferry_pets',
                count,
            })}`;
        case JourneyExtraType.Meal:
            return `${count}x ${t({
                key: 'shared.ferry_meal',
                keyPlural: 'shared.ferry_meals',
                count,
            })}`;
        case JourneyExtraType.Lounge:
            return `${count}x ${t({
                key: 'shared.ferry_lounge',
                keyPlural: 'shared.ferry_lounges',
                count,
            })}`;
        default:
            return getDetailedDescription(items);
    }
}

const getIcon = (item: JourneyExtra | JourneyExtraVehicle): IconType | undefined => {
    const vehicleIcon = (vehicle: JourneyExtraVehicle) => {
        switch (vehicle.vehicleType) {
            case FerryVehicleType.MotorBike:
                return 'MotorBike';
            case FerryVehicleType.Bicycle:
                return 'Bicycle';
            case FerryVehicleType.MiniBus4mHigh6mLong:
            case FerryVehicleType.MiniBus8mLong:
                return 'MiniBus';
            case FerryVehicleType.Vehicle6mLong:
                return 'Van';
            default:
                return 'Car';
        }
    };

    switch (item.type) {
        case JourneyExtraType.Accommodation:
        case JourneyExtraType.Cabin:
        case JourneyExtraType.Seat:
            return 'Bed';
        case JourneyExtraType.Pet:
            return 'Paw';
        case JourneyExtraType.Meal:
            return 'Meal';
        case JourneyExtraType.Lounge:
            return 'Ticket';
        case JourneyExtraType.Vehicle:
            return vehicleIcon(item as JourneyExtraVehicle);
        default:
            return undefined;
    }
};
